import type { ProjectNode } from '../types/project';
import { nodeEffectiveRadius } from './routeGeometry';
import type { ProjectSettings } from '../types/project';

// ------------------------------------------------------------
// Canvas marquee selection helpers
// ------------------------------------------------------------

export interface CanvasRect {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export type MarqueeMode = 'window' | 'crossing';

export function normalizeCanvasRect(rect: CanvasRect): CanvasRect {
  return {
    x1: Math.min(rect.x1, rect.x2),
    y1: Math.min(rect.y1, rect.y2),
    x2: Math.max(rect.x1, rect.x2),
    y2: Math.max(rect.y1, rect.y2),
  };
}

/**
 * CAD-style marquee mode from drag direction on screen.
 * Left-to-right = window (fully contained); right-to-left = crossing (touches).
 */
export function resolveMarqueeModeFromDrag(
  startX: number,
  startY: number,
  endX: number,
  endY: number,
): MarqueeMode {
  const dx = endX - startX;
  const dy = endY - startY;
  if (Math.abs(dx) < 1e-6 && Math.abs(dy) < 1e-6) return 'window';
  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx >= 0 ? 'window' : 'crossing';
  }
  return dy >= 0 ? 'window' : 'crossing';
}

/** @deprecated Prefer resolveMarqueeModeFromDrag with screen-space drag points. */
export function resolveMarqueeMode(rect: CanvasRect): MarqueeMode {
  return resolveMarqueeModeFromDrag(rect.x1, rect.y1, rect.x2, rect.y2);
}

export function isWindowSelection(rect: CanvasRect): boolean {
  return resolveMarqueeMode(rect) === 'window';
}

function nodeHitRadius(
  node: ProjectNode,
  displayScale: number,
  zoom: number,
  settings?: ProjectSettings,
): number {
  return nodeEffectiveRadius(node, displayScale, zoom, settings);
}

export function nodeIntersectsRect(
  node: ProjectNode,
  rect: CanvasRect,
  displayScale = 1,
  zoom = 1,
  settings?: ProjectSettings,
): boolean {
  const box = normalizeCanvasRect(rect);
  const radius = nodeHitRadius(node, displayScale, zoom, settings);
  return (
    node.x + radius >= box.x1 &&
    node.x - radius <= box.x2 &&
    node.y + radius >= box.y1 &&
    node.y - radius <= box.y2
  );
}

export function nodeContainedInRect(
  node: ProjectNode,
  rect: CanvasRect,
  displayScale = 1,
  zoom = 1,
  settings?: ProjectSettings,
): boolean {
  const box = normalizeCanvasRect(rect);
  const radius = nodeHitRadius(node, displayScale, zoom, settings);
  return (
    node.x - radius >= box.x1 &&
    node.x + radius <= box.x2 &&
    node.y - radius >= box.y1 &&
    node.y + radius <= box.y2
  );
}

export function nodesInRect(
  nodes: ProjectNode[],
  rect: CanvasRect,
  mode: MarqueeMode,
  displayScale = 1,
  zoom = 1,
  settings?: ProjectSettings,
): string[] {
  const box = normalizeCanvasRect(rect);
  if (box.x2 - box.x1 < 2 && box.y2 - box.y1 < 2) return [];
  const test = mode === 'window' ? nodeContainedInRect : nodeIntersectsRect;
  return nodes
    .filter((node) => test(node, box, displayScale, zoom, settings))
    .map((node) => node.id);
}

function pointInRect(x: number, y: number, box: CanvasRect): boolean {
  return x >= box.x1 && x <= box.x2 && y >= box.y1 && y <= box.y2;
}

function orientation(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
): number {
  const value = (by - ay) * (cx - bx) - (bx - ax) * (cy - by);
  if (Math.abs(value) < 1e-9) return 0;
  return value > 0 ? 1 : 2;
}

function onSegment(px: number, py: number, qx: number, qy: number, rx: number, ry: number): boolean {
  return (
    qx <= Math.max(px, rx) + 1e-9 &&
    qx + 1e-9 >= Math.min(px, rx) &&
    qy <= Math.max(py, ry) + 1e-9 &&
    qy + 1e-9 >= Math.min(py, ry)
  );
}

function segmentsIntersect(
  ax1: number,
  ay1: number,
  ax2: number,
  ay2: number,
  bx1: number,
  by1: number,
  bx2: number,
  by2: number,
): boolean {
  const o1 = orientation(ax1, ay1, ax2, ay2, bx1, by1);
  const o2 = orientation(ax1, ay1, ax2, ay2, bx2, by2);
  const o3 = orientation(bx1, by1, bx2, by2, ax1, ay1);
  const o4 = orientation(bx1, by1, bx2, by2, ax2, ay2);

  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && onSegment(ax1, ay1, bx1, by1, ax2, ay2)) return true;
  if (o2 === 0 && onSegment(ax1, ay1, bx2, by2, ax2, ay2)) return true;
  if (o3 === 0 && onSegment(bx1, by1, ax1, ay1, bx2, by2)) return true;
  if (o4 === 0 && onSegment(bx1, by1, ax2, ay2, bx2, by2)) return true;
  return false;
}

function segmentIntersectsRect(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  box: CanvasRect,
): boolean {
  if (pointInRect(x1, y1, box) || pointInRect(x2, y2, box)) return true;
  const edges: Array<[number, number, number, number]> = [
    [box.x1, box.y1, box.x2, box.y1],
    [box.x2, box.y1, box.x2, box.y2],
    [box.x2, box.y2, box.x1, box.y2],
    [box.x1, box.y2, box.x1, box.y1],
  ];
  return edges.some(([ex1, ey1, ex2, ey2]) =>
    segmentsIntersect(x1, y1, x2, y2, ex1, ey1, ex2, ey2),
  );
}

export function polylineIntersectsRect(points: number[], rect: CanvasRect): boolean {
  const box = normalizeCanvasRect(rect);
  if (points.length < 4) return false;
  for (let i = 0; i < points.length - 2; i += 2) {
    if (
      segmentIntersectsRect(
        points[i],
        points[i + 1],
        points[i + 2],
        points[i + 3],
        box,
      )
    ) {
      return true;
    }
  }
  return false;
}

function segmentContainedInRect(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  box: CanvasRect,
): boolean {
  return (
    pointInRect(x1, y1, box) &&
    pointInRect(x2, y2, box)
  );
}

export function polylineContainedInRect(points: number[], rect: CanvasRect): boolean {
  const box = normalizeCanvasRect(rect);
  if (points.length < 4) return false;
  for (let i = 0; i < points.length - 2; i += 2) {
    if (
      !segmentContainedInRect(
        points[i],
        points[i + 1],
        points[i + 2],
        points[i + 3],
        box,
      )
    ) {
      return false;
    }
  }
  return true;
}

export function routesInRect(
  routePointsById: Map<string, number[]>,
  rect: CanvasRect,
  mode: MarqueeMode,
): string[] {
  const box = normalizeCanvasRect(rect);
  if (box.x2 - box.x1 < 2 && box.y2 - box.y1 < 2) return [];
  const test = mode === 'window' ? polylineContainedInRect : polylineIntersectsRect;
  const ids: string[] = [];
  for (const [routeId, points] of routePointsById) {
    if (test(points, box)) ids.push(routeId);
  }
  return ids;
}

export function selectionBounds(
  nodes: ProjectNode[],
  displayScale = 1,
  zoom = 1,
  settings?: ProjectSettings,
): { x: number; y: number } | null {
  if (nodes.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const node of nodes) {
    const radius = nodeHitRadius(node, displayScale, zoom, settings);
    minX = Math.min(minX, node.x - radius);
    minY = Math.min(minY, node.y - radius);
    maxX = Math.max(maxX, node.x + radius);
    maxY = Math.max(maxY, node.y + radius);
  }
  return { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
}
